/**
 * @fileoverview TemplatePreview component for displaying template documents.
 *
 * This component fetches and renders a preview of a template document,
 * supporting both PDF and DOCX formats. It handles loading states,
 * errors, and responsive sizing.
 */

import { useMeasure } from '@uidotdev/usehooks';
import { FileText } from 'lucide-react';
import React, { useEffect } from 'react';

import type { DocxMergeFieldValue } from '../hooks/mergeFields';
import { useTemplatePreview } from '../hooks/useTemplatePreview';
import { PdfRenderer } from '../pdf/renderer/PdfFormRenderer';
import { DocumentLoadingState } from './components/DocumentLoadingState';

/**
 * Props for the TemplatePreview component.
 */
interface TemplatePreviewProps {
  /** ID of the template to preview */
  templateId: string;
  /** ID of the matter to get context data from (optional) */
  matterId?: string;
  /** Matter context to use for preview. Can include arrays for Jinja2 iteration. */
  matterContext?: Record<string, DocxMergeFieldValue | undefined>;
  /** Overrides to use for preview */
  overrides: Record<string, string>;
  /** ID of the medical note for specific provider context (optional) */
  medicalNoteId?: string;
  onPdfBlobChanged?: (blob: Blob) => void;
  fitPage?: boolean;
  maxPages?: number;
}

/**
 * TemplatePreview displays a visual preview of a template document.
 *
 * This component:
 * - Fetches template data and renders it as a PDF preview
 * - Uses real matter context data when matterId is provided
 * - Falls back to provided matterContext for backward compatibility
 * - Handles loading states with a spinner
 * - Shows error states when preview generation fails
 * - Automatically measures container width for responsive rendering
 * - Supports both PDF and DOCX templates (DOCX is converted to PDF for preview)
 *
 * The preview is rendered using the PdfRenderer component, which handles
 * the actual document display and field visualization.
 *
 * @param props - Component props
 */
export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  templateId,
  matterId,
  matterContext,
  overrides,
  medicalNoteId,
  onPdfBlobChanged,
  fitPage = false,
  maxPages,
}) => {
  // Measure available width early to comply with React hook rules
  // This ensures the PDF renders at an appropriate size for the container
  const [containerRef, { width, height }] = useMeasure();

  // Fetch template preview data (blob, fields, type) and loading/error states
  // Pass matterId and medicalNoteId to fetch real matter context data with specific provider info
  const { previewBlob, placedFields, templateType, isLoading, error } =
    useTemplatePreview(
      templateId,
      matterId,
      matterContext,
      overrides,
      medicalNoteId,
    );

  // Notify parent when blob changes
  useEffect(() => {
    if (previewBlob && onPdfBlobChanged) {
      onPdfBlobChanged(previewBlob);
    }
  }, [previewBlob, onPdfBlobChanged]);

  // Show loading spinner while template data is being fetched
  if (isLoading) {
    return <DocumentLoadingState message="Loading template…" />;
  }

  // Show error state if preview generation failed or no blob was returned
  if (error || !previewBlob) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] py-12">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 border border-red-100 mb-6">
          <FileText className="h-8 w-8 text-red-600" />
        </div>
        <p className="text-sm font-medium text-gray-900 mb-2">Failed to load</p>
        <p className="text-xs text-gray-500">
          Unable to prepare a preview for this template.
        </p>
      </div>
    );
  }

  // Use measured width or fallback to reasonable default (capped for better fit)
  const stableWidth = Math.min(width || 700, 700);
  const stableHeight = Math.max((height || 520) - 16, 220);

  return (
    <div ref={containerRef} className="h-full w-full">
      {templateType === 'PDF' && placedFields ? (
        // For PDF templates with fields, render with field visualization
        // Re-use PdfRenderer because fields are already drawn by the pipeline.
        <PdfRenderer
          file={previewBlob}
          renderWidth={fitPage ? undefined : stableWidth}
          renderHeight={fitPage ? stableHeight : undefined}
          maxPages={maxPages}
        />
      ) : (
        // For DOCX templates or PDFs without fields, render simple preview
        <PdfRenderer
          file={previewBlob}
          renderWidth={fitPage ? undefined : stableWidth}
          renderHeight={fitPage ? stableHeight : undefined}
          maxPages={maxPages}
        />
      )}
    </div>
  );
};
